// frontend/src/components/engineer/pages/EngineerDutyResumePage.tsx
// Engineer Portal - Duty Resume Tracking for Annual Leave
// PHOENICIAN TECHNICAL SERVICES LLC

import React, { useState, useCallback, useEffect } from 'react';
import { AuthUser } from '../../../services/auth.service';
import { annualLeaveService, AnnualLeaveApplicationRecord, DutyResumeStatus } from '../../../services/annualLeave.service';
import styles from './EngineerDutyResumePage.module.css';

interface Props { user?: AuthUser; onNavigate?: (page: string) => void; }
type Tab = 'pending' | 'overdue' | 'today' | 'search';

const EngineerDutyResumePage: React.FC<Props> = ({ user, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<Tab>('pending');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingResume, setPendingResume] = useState<AnnualLeaveApplicationRecord[]>([]);
  const [overdueReturns, setOverdueReturns] = useState<AnnualLeaveApplicationRecord[]>([]);
  const [todayReturns, setTodayReturns] = useState<AnnualLeaveApplicationRecord[]>([]);
  const [searchResults, setSearchResults] = useState<AnnualLeaveApplicationRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<AnnualLeaveApplicationRecord | null>(null);
  const [modalAction, setModalAction] = useState<'resume' | 'extend' | 'absent' | null>(null);
  const [actualReturnDate, setActualReturnDate] = useState('');
  const [dutyResumedDate, setDutyResumedDate] = useState('');
  const [dutyResumeRemarks, setDutyResumeRemarks] = useState('');
  const [extensionDays, setExtensionDays] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  const [newExpectedReturnDate, setNewExpectedReturnDate] = useState('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [pending, overdue, today] = await Promise.all([
        annualLeaveService.getPendingDutyResume(),
        annualLeaveService.getOverdueReturns(),
        annualLeaveService.getReturnsExpectedToday()
      ]);
      setPendingResume(pending);
      setOverdueReturns(overdue);
      setTodayReturns(today);
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const results = await annualLeaveService.searchForDutyResume(searchTerm.trim());
      setSearchResults(results);
      setActiveTab('search');
    } catch (err) { console.error(err); } finally { setIsSearching(false); }
  }, [searchTerm]);

  const openModal = (leave: AnnualLeaveApplicationRecord, action: 'resume' | 'extend' | 'absent') => {
    setSelectedLeave(leave);
    setModalAction(action);
    setShowModal(true);
    const today = new Date().toISOString().split('T')[0];
    setActualReturnDate(today);
    setDutyResumedDate(today);
    setDutyResumeRemarks('');
    setExtensionDays('');
    setExtensionReason('');
    setNewExpectedReturnDate('');
  };

  const handleMarkResumed = async () => {
    if (!selectedLeave || !user || !actualReturnDate || !dutyResumedDate) { setError('Fill required fields'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const result = await annualLeaveService.markDutyResumed(
        selectedLeave.applicationId, user.employeeNo || user.uid, user.name || 'Engineer',
        new Date(actualReturnDate), new Date(dutyResumedDate), dutyResumeRemarks
      );
      if (result.success) { setSuccess(`✅ ${selectedLeave.employeeName} marked as resumed`); setShowModal(false); loadData(); }
      else setError(result.error || 'Failed');
    } catch (err: any) { setError(err.message); } finally { setIsLoading(false); }
  };

  const handleExtendLeave = async () => {
    if (!selectedLeave || !user || !extensionDays || !extensionReason || !newExpectedReturnDate) { setError('Fill required fields'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const result = await annualLeaveService.extendLeave(
        selectedLeave.applicationId, new Date(newExpectedReturnDate), parseInt(extensionDays),
        extensionReason, user.employeeNo || user.uid, user.name || 'Engineer'
      );
      if (result.success) { setSuccess(`✅ Leave extended for ${selectedLeave.employeeName}`); setShowModal(false); loadData(); }
      else setError(result.error || 'Failed');
    } catch (err: any) { setError(err.message); } finally { setIsLoading(false); }
  };

  const handleMarkNotReturned = async () => {
    if (!selectedLeave || !user) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await annualLeaveService.markNotReturned(
        selectedLeave.applicationId, user.employeeNo || user.uid, user.name || 'Engineer', dutyResumeRemarks
      );
      if (result.success) { setSuccess(`⚠️ ${selectedLeave.employeeName} marked as not returned`); setShowModal(false); loadData(); }
      else setError(result.error || 'Failed');
    } catch (err: any) { setError(err.message); } finally { setIsLoading(false); }
  };

  const fmt = (d: Date | undefined) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const fmtLong = (d: Date | undefined) => d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const getStatusBadge = (status: DutyResumeStatus) => {
    const badges: Record<string, { cls: string; text: string }> = {
      pending: { cls: styles.badgePending, text: 'Pending' },
      resumed: { cls: styles.badgeResumed, text: 'Resumed' },
      extended: { cls: styles.badgeExtended, text: 'Extended' },
      not_returned: { cls: styles.badgeAbsent, text: 'Not Returned' },
      absent: { cls: styles.badgeAbsent, text: 'Absent' }
    };
    const b = badges[status] || { cls: styles.badgePending, text: status };
    return <span className={b.cls}>{b.text}</span>;
  };

  const isOverdue = (leave: AnnualLeaveApplicationRecord) => {
    const exp = leave.newExpectedReturnDate || leave.expectedReturnDate;
    if (!exp) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return new Date(exp) < today;
  };

  const getDaysOverdue = (leave: AnnualLeaveApplicationRecord) => {
    const exp = leave.newExpectedReturnDate || leave.expectedReturnDate;
    if (!exp) return 0;
    return Math.max(0, Math.floor((new Date().getTime() - new Date(exp).getTime()) / 86400000));
  };

  const getCurrentList = () => {
    switch (activeTab) {
      case 'pending': return pendingResume;
      case 'overdue': return overdueReturns;
      case 'today': return todayReturns;
      case 'search': return searchResults;
      default: return [];
    }
  };

  const renderLeaveCard = (leave: AnnualLeaveApplicationRecord) => (
    <div key={leave.applicationId} className={`${styles.leaveCard} ${isOverdue(leave) ? styles.overdueCard : ''}`}>
      <div className={styles.cardHeader}>
        <div className={styles.employeeInfo}>
          <span className={styles.employeeName}>{leave.employeeName}</span>
          <span className={styles.employeePin}>PIN: {leave.employeePin}</span>
        </div>
        {getStatusBadge(leave.dutyResumeStatus)}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}><span className={styles.label}>Leave Period</span><span className={styles.value}>{fmt(leave.startDate)} - {fmt(leave.endDate)}</span></div>
          <div className={styles.infoItem}><span className={styles.label}>Duration</span><span className={styles.value}>{leave.totalDays} days</span></div>
          <div className={styles.infoItem}><span className={styles.label}>Outbound</span><span className={styles.value}>{leave.departureAirport?.iataCode} → {leave.arrivalAirport?.iataCode}</span></div>
          <div className={styles.infoItem}><span className={styles.label}>Return</span><span className={styles.value}>{leave.returnDepartureAirport?.iataCode} → {leave.returnArrivalAirport?.iataCode}</span></div>
          <div className={`${styles.infoItem} ${styles.highlight}`}>
            <span className={styles.label}>Expected Return</span>
            <span className={`${styles.value} ${isOverdue(leave) ? styles.overdueValue : ''}`}>
              {fmtLong(leave.newExpectedReturnDate || leave.expectedReturnDate)}
              {isOverdue(leave) && <span className={styles.overdueBadge}>{getDaysOverdue(leave)}d overdue</span>}
            </span>
          </div>
          {leave.dutyResumedDate && <div className={styles.infoItem}><span className={styles.label}>Resumed</span><span className={styles.value}>{fmt(leave.dutyResumedDate)}</span></div>}
        </div>
        {leave.returnRemarks && <div className={styles.remarks}><span className={styles.label}>Remarks:</span> {leave.returnRemarks}</div>}
        {leave.extensionReason && <div className={styles.extensionInfo}>Extended {leave.extensionDays}d: {leave.extensionReason}</div>}
      </div>
      {(leave.dutyResumeStatus === 'pending' || leave.dutyResumeStatus === 'extended') && (
        <div className={styles.cardActions}>
          <button onClick={() => openModal(leave, 'resume')} className={styles.btnResume}>Mark Resumed</button>
          {leave.dutyResumeStatus === 'pending' && <button onClick={() => openModal(leave, 'extend')} className={styles.btnExtend}>Extend</button>}
          <button onClick={() => openModal(leave, 'absent')} className={styles.btnAbsent}>Not Returned</button>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}><button onClick={() => onNavigate?.('dashboard')} className={styles.backButton}>← Back</button></div>
        <div className={styles.headerCenter}><h1>Duty Resume Tracking</h1><p>Track employee returns from annual leave</p></div>
        <div className={styles.headerRight}><button onClick={loadData} className={styles.refreshButton} disabled={isLoading}>🔄</button></div>
      </div>

      <div className={styles.statsRow}>
        <div className={`${styles.statCard} ${styles.statPending}`}><span className={styles.statNumber}>{pendingResume.length}</span><span className={styles.statLabel}>Pending</span></div>
        <div className={`${styles.statCard} ${styles.statOverdue}`}><span className={styles.statNumber}>{overdueReturns.length}</span><span className={styles.statLabel}>Overdue</span></div>
        <div className={`${styles.statCard} ${styles.statToday}`}><span className={styles.statNumber}>{todayReturns.length}</span><span className={styles.statLabel}>Today</span></div>
      </div>

      <div className={styles.searchSection}>
        <div className={styles.searchInputGroup}>
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by PIN, name, or ID..." className={styles.searchInput} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} />
          <button onClick={handleSearch} disabled={isSearching} className={styles.searchButton}>{isSearching ? '...' : '🔍'}</button>
        </div>
      </div>

      {error && <div className={styles.errorAlert}><span>⚠️ {error}</span><button onClick={() => setError(null)}>✕</button></div>}
      {success && <div className={styles.successAlert}><span>{success}</span><button onClick={() => setSuccess(null)}>✕</button></div>}

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeTab === 'pending' ? styles.activeTab : ''}`} onClick={() => setActiveTab('pending')}>All ({pendingResume.length})</button>
        <button className={`${styles.tab} ${activeTab === 'overdue' ? styles.activeTab : ''} ${overdueReturns.length > 0 ? styles.alertTab : ''}`} onClick={() => setActiveTab('overdue')}>🚨 Overdue ({overdueReturns.length})</button>
        <button className={`${styles.tab} ${activeTab === 'today' ? styles.activeTab : ''}`} onClick={() => setActiveTab('today')}>📅 Today ({todayReturns.length})</button>
        {searchResults.length > 0 && <button className={`${styles.tab} ${activeTab === 'search' ? styles.activeTab : ''}`} onClick={() => setActiveTab('search')}>🔍 ({searchResults.length})</button>}
      </div>

      <div className={styles.content}>
        {isLoading ? <div className={styles.loading}>Loading...</div> : getCurrentList().length === 0 ? (
          <div className={styles.emptyState}><span className={styles.emptyIcon}>📭</span><h3>No Records</h3></div>
        ) : <div className={styles.cardList}>{getCurrentList().map(renderLeaveCard)}</div>}
      </div>

      {showModal && selectedLeave && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{modalAction === 'resume' && '✅ Mark Resumed'}{modalAction === 'extend' && '📅 Extend'}{modalAction === 'absent' && '⚠️ Not Returned'}</h3>
              <button onClick={() => setShowModal(false)} className={styles.modalClose}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.employeeSummary}>
                <div><span>Employee:</span><strong>{selectedLeave.employeeName}</strong></div>
                <div><span>PIN:</span><strong>{selectedLeave.employeePin}</strong></div>
                <div><span>Expected:</span><strong>{fmtLong(selectedLeave.newExpectedReturnDate || selectedLeave.expectedReturnDate)}</strong></div>
              </div>
              {modalAction === 'resume' && (
                <div className={styles.form}>
                  <div className={styles.formGroup}><label>Actual Return *</label><input type="date" value={actualReturnDate} onChange={(e) => setActualReturnDate(e.target.value)} className={styles.formInput} /></div>
                  <div className={styles.formGroup}><label>Duty Resumed *</label><input type="date" value={dutyResumedDate} onChange={(e) => setDutyResumedDate(e.target.value)} className={styles.formInput} /></div>
                  <div className={styles.formGroup}><label>Remarks</label><textarea value={dutyResumeRemarks} onChange={(e) => setDutyResumeRemarks(e.target.value)} rows={2} className={styles.formTextarea} /></div>
                </div>
              )}
              {modalAction === 'extend' && (
                <div className={styles.form}>
                  <div className={styles.formGroup}><label>Extension Days *</label><input type="number" value={extensionDays} onChange={(e) => { setExtensionDays(e.target.value); if (e.target.value && selectedLeave.expectedReturnDate) { const d = new Date(selectedLeave.newExpectedReturnDate || selectedLeave.expectedReturnDate); d.setDate(d.getDate() + parseInt(e.target.value)); setNewExpectedReturnDate(d.toISOString().split('T')[0]); }}} min="1" className={styles.formInput} /></div>
                  <div className={styles.formGroup}><label>New Return Date *</label><input type="date" value={newExpectedReturnDate} onChange={(e) => setNewExpectedReturnDate(e.target.value)} className={styles.formInput} /></div>
                  <div className={styles.formGroup}><label>Reason *</label><textarea value={extensionReason} onChange={(e) => setExtensionReason(e.target.value)} rows={2} className={styles.formTextarea} /></div>
                </div>
              )}
              {modalAction === 'absent' && (
                <div className={styles.form}>
                  <div className={styles.warningBox}><span>⚠️</span><p>Mark as not returned. HR will be notified.</p></div>
                  <div className={styles.formGroup}><label>Remarks</label><textarea value={dutyResumeRemarks} onChange={(e) => setDutyResumeRemarks(e.target.value)} rows={2} className={styles.formTextarea} /></div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button onClick={() => setShowModal(false)} className={styles.btnCancel}>Cancel</button>
              {modalAction === 'resume' && <button onClick={handleMarkResumed} disabled={isLoading} className={styles.btnConfirmResume}>{isLoading ? '...' : '✅ Confirm'}</button>}
              {modalAction === 'extend' && <button onClick={handleExtendLeave} disabled={isLoading} className={styles.btnConfirmExtend}>{isLoading ? '...' : '📅 Confirm'}</button>}
              {modalAction === 'absent' && <button onClick={handleMarkNotReturned} disabled={isLoading} className={styles.btnConfirmAbsent}>{isLoading ? '...' : '⚠️ Confirm'}</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineerDutyResumePage;